import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import CadastroRapidoModal from "../../components/estoque/CadastroRapidoModal";
import SelectAutocomplete from "../../components/SelectAutocomplete";
import {
  entradaManualService,
  pedidoCompraService,
} from "../../services/apiService";
import {
  Button,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmpty,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
  EmptyState,
  LoadingState,
  SectionCard,
  TextInput,
} from "../../components/ui";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { useNotification } from "../../hooks/useNotification";

const novoId = () => crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;

export default function EntradaPedidoLote() {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useNotification();
  const confirmar = useConfirmDialog();
  const [pedidoId, setPedidoId] = useState("");
  const [quantidades, setQuantidades] = useState({});
  const [observacoes, setObservacoes] = useState({});
  const [cadastroRapido, setCadastroRapido] = useState(null);

  const pedidos = useQuery({
    queryKey: ["pedidos-entrada"],
    queryFn: () => entradaManualService.pedidos(""),
    retry: 0,
  });
  const pedido = useMemo(
    () => (pedidos.data || []).find((item) => item.id === pedidoId),
    [pedidos.data, pedidoId],
  );
  const registro = useMutation({
    mutationFn: (dados) => entradaManualService.registrarLote(dados),
    onSuccess: (resultado) => {
      showSuccess(
        `${resultado.length} ${
          resultado.length === 1 ? "entrada registrada" : "entradas registradas"
        }.`,
      );
      setPedidoId("");
      setQuantidades({});
      setObservacoes({});
      queryClient.invalidateQueries({ queryKey: ["pedidos-entrada"] });
      queryClient.invalidateQueries({
        queryKey: ["entradas-produtos", "historico"],
      });
    },
    onError: (erro) => showError(erro.message),
  });

  const selecionarPedido = (id) => {
    setPedidoId(id);
    setQuantidades({});
    setObservacoes({});
  };

  const salvar = async (evento) => {
    evento.preventDefault();
    if (!pedido) return;

    const itens = pedido.itens
      .filter((item) => item.produtoId && Number(quantidades[item.id] || 0) > 0)
      .map((item) => ({
        pedidoItemId: item.id,
        produtoId: item.produtoId,
        quantidade: Number(quantidades[item.id]),
        observacao: observacoes[item.id] || "",
        expectedVersion: item.versaoSaldo || 0,
      }));

    if (!itens.length) {
      showError("Informe a quantidade recebida em ao menos um item.");
      return;
    }

    const confirmado = await confirmar({
      title: "Registrar recebimento",
      description: `Serão registradas ${itens.length} ${
        itens.length === 1 ? "linha" : "linhas"
      } deste pedido.`,
      variant: "primary",
    });
    if (!confirmado) return;

    registro.mutate({
      pedidoId: pedido.id,
      requestId: novoId(),
      itens,
    });
  };

  const abrirCadastroProduto = (item) => {
    setCadastroRapido({ item });
  };

  const concluirCadastroProduto = async (produto) => {
    const item = cadastroRapido?.item;
    setCadastroRapido(null);
    if (!item || !pedido) return;

    try {
      await pedidoCompraService.converterItemLivreEmNovoProduto(
        pedido.id,
        item.id,
        {
          produtoId: produto.id,
          expectedVersion: pedido.versao,
        },
      );
      await queryClient.invalidateQueries({
        queryKey: ["pedidos-entrada"],
      });
      showSuccess("Produto cadastrado e vinculado ao item do pedido.");
    } catch (erro) {
      showError(erro.message);
    }
  };

  if (pedidos.isLoading) {
    return <LoadingState message="Carregando pedidos..." />;
  }

  if (pedidos.isError) {
    return (
      <EmptyState
        title="Não foi possível carregar os pedidos"
        description="Atualize e tente novamente."
      />
    );
  }

  return (
    <>
      <SectionCard>
        <form onSubmit={salvar} className="space-y-5">
          <div>
            <h2 className="font-semibold text-gray-900">Entrada por pedido</h2>
            <p className="mt-1 text-sm text-gray-500">
              Informe em cada linha somente o que foi entregue agora. Você pode
              registrar todos os itens de uma vez.
            </p>
          </div>

          <label className="block max-w-xl text-sm font-medium text-gray-700">
            Pedido finalizado
            <SelectAutocomplete
              value={pedidoId}
              onChange={selecionarPedido}
              placeholder="Selecione o pedido"
              options={(pedidos.data || []).map((item) => ({
                value: item.id,
                label: `Pedido #${item.numero} · ${
                  item.fornecedorNome || "Sem fornecedor"
                }`,
              }))}
              emptyMessage="Nenhum pedido com itens pendentes."
            />
          </label>

          {pedido && (
            <>
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                Fornecedor:{" "}
                <strong>{pedido.fornecedorNome || "Não informado"}</strong> ·{" "}
                {pedido.itens.length} item(ns) pendente(s)
              </div>

              <DataTable>
                <DataTableHead>
                  <tr>
                    <DataTableHeaderCell>Produto</DataTableHeaderCell>
                    <DataTableHeaderCell className="text-right">
                      Solicitado
                    </DataTableHeaderCell>
                    <DataTableHeaderCell className="text-right">
                      Já recebido
                    </DataTableHeaderCell>
                    <DataTableHeaderCell className="text-right">
                      Pendente
                    </DataTableHeaderCell>
                    <DataTableHeaderCell className="text-right">
                      Receber agora
                    </DataTableHeaderCell>
                    <DataTableHeaderCell>Observação</DataTableHeaderCell>
                  </tr>
                </DataTableHead>
                <DataTableBody>
                  {pedido.itens.length ? (
                    pedido.itens.map((item) => (
                      <Linha
                        key={item.id}
                        item={item}
                        quantidade={quantidades[item.id] ?? ""}
                        observacao={observacoes[item.id] ?? ""}
                        onQuantidade={(valor) =>
                          setQuantidades((atual) => ({
                            ...atual,
                            [item.id]: valor,
                          }))
                        }
                        onObservacao={(valor) =>
                          setObservacoes((atual) => ({
                            ...atual,
                            [item.id]: valor,
                          }))
                        }
                        onCadastrarProduto={() => abrirCadastroProduto(item)}
                      />
                    ))
                  ) : (
                    <DataTableEmpty
                      colSpan={6}
                      message="Não há itens pendentes neste pedido."
                    />
                  )}
                </DataTableBody>
              </DataTable>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="success"
                  icon={Save}
                  disabled={registro.isPending}
                >
                  {registro.isPending
                    ? "Registrando..."
                    : "Registrar recebimento"}
                </Button>
              </div>
            </>
          )}
        </form>
      </SectionCard>

      <CadastroRapidoModal
        open={Boolean(cadastroRapido)}
        tipo="produto"
        produtoInicial={{
          descricao: cadastroRapido?.item?.descricao || "",
          unidadeSimbolo: cadastroRapido?.item?.unidadeSimbolo || "",
          fornecedorId: pedido?.fornecedorId || "",
          codigo: cadastroRapido?.item?.codigo || "",
        }}
        onClose={() => setCadastroRapido(null)}
        onCreated={concluirCadastroProduto}
      />
    </>
  );
}

function Linha({
  item,
  quantidade,
  observacao,
  onQuantidade,
  onObservacao,
  onCadastrarProduto,
}) {
  if (!item.produtoId) {
    return (
      <DataTableRow>
        <DataTableCell colSpan={6}>
          <strong>{item.descricao}</strong>
          <p className="mt-1 text-sm text-amber-800">
            Produto livre: cadastre-o antes de registrar a entrada.
          </p>
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="mt-2"
            onClick={onCadastrarProduto}
          >
            Cadastrar produto
          </Button>
        </DataTableCell>
      </DataTableRow>
    );
  }

  return (
    <DataTableRow>
      <DataTableCell>
        <strong>{item.descricao}</strong>
        <small className="block text-gray-500">
          {item.codigo || "Sem código"}
        </small>
      </DataTableCell>
      <DataTableCell className="text-right">
        {item.quantidadeSolicitada} {item.unidadeSimbolo}
      </DataTableCell>
      <DataTableCell className="text-right">
        {item.quantidadeRecebida} {item.unidadeSimbolo}
      </DataTableCell>
      <DataTableCell className="text-right">
        {item.pendencia} {item.unidadeSimbolo}
      </DataTableCell>
      <DataTableCell className="text-right">
        <TextInput
          type="number"
          min="0"
          max={item.pendencia}
          value={quantidade}
          onChange={(e) => onQuantidade(e.target.value)}
          className="ml-auto w-24 text-right"
          aria-label={`Quantidade recebida de ${item.descricao}`}
        />
      </DataTableCell>
      <DataTableCell>
        <TextInput
          value={observacao}
          maxLength={1000}
          onChange={(e) => onObservacao(e.target.value)}
          placeholder="Opcional"
          aria-label={`Observação de ${item.descricao}`}
        />
      </DataTableCell>
    </DataTableRow>
  );
}
